import { useContext, useEffect, useReducer, useState } from 'react';

import { UserDetailContext, UserDetailContextProps } from '../../context/userDetail/userDetailContext';
import { GRID_ACTIONS, gridReducer, IGridReducerState } from '../../reducer/gridReducer/gridReducer';
import ScheduledReportPage from './ScheduledReportPage';
import { getSelectedCustomerId } from '../../helpers/customer';
import { isTpiUser } from '../../helpers/tpiUser';
import { useAuthState } from '../../providers/authProvider';
import { getInitialType } from '../../utils/getInitialTypes';
import { SearchDetailContext, SearchDetailContextProps } from '../../context/searchDetail/searchDetailContext';
import { RoutingToTypes } from '../search/SearchActions/SearchActions';

import { getScheduledReportRequests } from '../../common/api/scheduledReportsApi';
import { getAllTpiUsers } from '../../common/api/tpiApi';

export type tpiEmailDetailOption = {
  id: string;
  email: string;
};

const ScheduledReport = () => {
  const [tpiEmailDetails, setTpiEmailDetails] = useState<Array<Api.IEmailRecipientScheduledReportRequest>>([]);

  const { userDetail } = useContext<UserDetailContextProps>(UserDetailContext);
  const searchContext = useContext<SearchDetailContextProps>(SearchDetailContext);
  const authContext = useAuthState();

  const initialState: IGridReducerState = {
    page: 1,
    currentType: '',
    data: {},
    initialSortField: 'ref',
    initialSortOrder: 'DESC',
    currentSortField: 'ref',
    currentSortOrder: 'ASC',
    isLoading: false,
    hasMoreData: true,
    selectedCustomerId: getSelectedCustomerId(userDetail, true),
  };

  const [state, dispatch] = useReducer(gridReducer, initialState);

  const isTpi = isTpiUser();
  const accountReferences = userDetail.customerAccounts;

  const loadData = async () => {
    dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: true });

    const data = getScheduledReportRequests(process.env.REACT_APP_API_URL, authContext, state.page);

    data.then((result: Common.IResult<any>) => {
      if (result && result.success) {
        var resultWithEmails = result.data.map((dataset: any, index: number) => ({
          ...dataset,
          emails: extractEmailAddresses(result.data[index].emailRecipientScheduledReportRequests),
        }));
        dispatch({ type: GRID_ACTIONS.ADD_DATA, payload: { data: resultWithEmails, page: state.page } });
      } else {
        dispatch({ type: GRID_ACTIONS.CLEAR_DATA });
      }
      dispatch({ type: GRID_ACTIONS.CHANGE_LOADING, payload: false });
    });
  };

  useEffect(() => {
    if (isTpi) {
      handleTpiUser();
    }
  }, []);

  useEffect(() => {
    if (searchContext.searchDetail != null && searchContext.searchDetail.searchText.length > 0) {
      // filter by search result

      if (
        searchContext.searchDetail.type &&
        searchContext.searchDetail.routingTo === RoutingToTypes.smartMeterConsent
      ) {
        const type = searchContext.searchDetail.type.toLowerCase();

        if (type === 'account') {
          dispatch({
            type: GRID_ACTIONS.FILTER_BY_CUSTOMER,
            payload: { essCustomerId: searchContext.searchDetail.accountId },
          });
        }
        // Clear Search Detail
        searchContext.setSearchDetail({ searchText: '' });
      }
    }
    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchContext.searchDetail.type]);

  // When navigating directly to url
  useEffect(() => {
    if (userDetail.essCustomerId) {
      initialState.currentType = getInitialType(userDetail.hasElectricity, userDetail.hasGas, userDetail.hasRenewable);

      const selectedCustomerAccount = userDetail?.selectedCustomerAccount;
      if (selectedCustomerAccount.length > 0) initialState.selectedCustomerId = selectedCustomerAccount;
      else initialState.selectedCustomerId = getSelectedCustomerId(userDetail, true);

      dispatch({ type: GRID_ACTIONS.REINIT, payload: initialState });
    }
    // TODO:  Might want to remove this and revisit this problem later
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetail.hasElectricity, userDetail.hasGas, userDetail.hasRenewable]);

  useEffect(() => {
    loadData();
  }, [state.page]);

  const handleCustomerFilterChange = (option: Common.IOption) => {
    dispatch({ type: GRID_ACTIONS.FILTER_BY_CUSTOMER, payload: { essCustomerId: option.value } });
  };

  const extractEmailAddresses = (recipients: Array<any>) => {
    let emailArray = [];

    for (var key in recipients) {
      emailArray.push(recipients[key].email);
    }

    return emailArray;
  };

  const handleLoadData = () => {
    dispatch({ type: GRID_ACTIONS.RESET_PAGE });
    if (state.page == initialState.page) {
      loadData();
    }
  };

  const handleTpiUser = async () => {
    const tpiData = await getAllTpiUsers(process.env.REACT_APP_API_URL, authContext);

    if (tpiData && tpiData.success) {
      let additionalTpiEmailsFull: Array<Api.IEmailRecipientScheduledReportRequest> = [];
      tpiData.data.map((item: any) => {
        const tpiEmailDetails: Api.IEmailRecipientScheduledReportRequest = {
          email: item.email,
          emailUserId: item.userId,
        };
        additionalTpiEmailsFull.push(tpiEmailDetails);
      });

      setTpiEmailDetails(additionalTpiEmailsFull);
    }
  };

  const handlePagingChange = () => {
    dispatch({ type: GRID_ACTIONS.INCREMENT_PAGE });
  };

  return (
    <ScheduledReportPage
      accountReferences={accountReferences}
      emails={tpiEmailDetails}
      currentUserEmail={userDetail.emailAddress}
      tpiUser={isTpi}
      gridData={state.gridData}
      isLoading={state.isLoading}
      hasMoreData={state.hasMoreData}
      handlePagingChange={handlePagingChange}
      handleCustomerFilterChange={handleCustomerFilterChange}
      handleLoadData={handleLoadData}
    ></ScheduledReportPage>
  );
};

export default ScheduledReport;
